/**
 * @file        src/modals/PaxModal.jsx
 * @description Passenger-details capture form. Ported from `#paxModal`
 *              (~line 2208) in the design HTML.
 * @author      Kinetika Labs
 * @created     2026-06-22
 * @changelog
 *   - 2026-08-17 Claude — MOBILE: viewport-height cap switched from `vh` to `svh`.
 *                          On a phone browser `100vh` is the height with the toolbars
 *                          HIDDEN, so a vh-capped sheet is taller than what is actually
 *                          on screen and its last row — the action buttons — sat under
 *                          the toolbar. `svh` is the always-visible height, so the
 *                          bottom of the sheet is reachable on every device. Desktop is
 *                          unaffected (svh == vh there).
 *   - 2026-07-29 Claude — Brand sweep: the teal→green CTA gradient / solid teal
 *                          button + active-tab fills → brand black (#1C1C1C) with
 *                          white text. Teal is kept for non-button accents (dots,
 *                          ticks, badges, step circles, calendar cells).
 *   - 2026-06-22 Claude — Initial port. `savePax()` is replaced with a
 *                         local state + `payload.onSave({ given, surname })`
 *                         callback so consumers (the flight-book page) can
 *                         feed `paxName` back into the booking store.
 */
import { useEffect, useState } from 'react';
import SelectMenu from '../components/SelectMenu.jsx';
import SingleDatePicker from '../components/SingleDatePicker.jsx';

// Nobody was born tomorrow — cap the DOB picker at today.
const d = new Date();
const TODAY_KEY = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

export default function PaxModal({ open, onClose, payload }) {
  // Start blank — the traveller fills their own details (no hardcoded identity).
  const [given, setGiven] = useState('');
  const [surname, setSurname] = useState('');
  const [gender, setGender] = useState('Male');
  const [dob, setDob] = useState('');
  const [nationality, setNationality] = useState('India');
  const [idType, setIdType] = useState('Passport');
  const [idNumber, setIdNumber] = useState('');

  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [open, onClose]);

  if (!open) return null;

  const save = () => {
    payload?.onSave?.({ given, surname, gender, dob, nationality, idType, idNumber });
    onClose?.();
  };

  return (
    <div className="fixed inset-0 z-[75] flex items-center justify-center p-4">
      <div onClick={onClose} className="absolute inset-0 bg-night/80 backdrop-blur-sm" />
      <div className="relative w-full max-w-2xl max-h-[88svh] overflow-y-auto rounded-3xl border border-line bg-panel p-6 no-scrollbar">
        <button onClick={onClose} className="absolute top-5 right-5 w-9 h-9 rounded-lg bg-night border border-line flex items-center justify-center hover:border-neon cursor-pointer">
          <svg viewBox="0 0 24 24" className="w-5 h-5 text-muted"><path fill="currentColor" d="m12 10.6 4.9-4.9 1.4 1.4-4.9 4.9 4.9 4.9-1.4 1.4-4.9-4.9-4.9 4.9-1.4-1.4 4.9-4.9-4.9-4.9 1.4-1.4z" /></svg>
        </button>
        <h2 className="font-display text-xl font-extrabold mb-5">Passenger details</h2>
        <div className="grid sm:grid-cols-2 gap-3">
          <div className="field bg-night/70 rounded-xl border border-line px-4 py-2.5">
            <label className="block text-[11px] font-semibold uppercase tracking-wide text-muted">Given name(s) *</label>
            <input type="text" value={given} onChange={(e) => setGiven(e.target.value)} className="w-full bg-transparent outline-none text-[15px]" />
          </div>
          <div className="field bg-night/70 rounded-xl border border-line px-4 py-2.5">
            <label className="block text-[11px] font-semibold uppercase tracking-wide text-muted">Surname *</label>
            <input type="text" value={surname} onChange={(e) => setSurname(e.target.value)} className="w-full bg-transparent outline-none text-[15px]" />
          </div>
          <div className="field bg-night/70 rounded-xl border border-line px-4 py-2.5">
            <label className="block text-[11px] font-semibold uppercase tracking-wide text-muted">Gender on ID *</label>
            <SelectMenu value={gender} onChange={setGender} options={['Male', 'Female']} />
          </div>
          {/* Branded picker, not the OS calendar — its year dropdown spans 100
              years back, which is what a date of birth actually needs. */}
          <SingleDatePicker label="Date of birth *" value={dob} onChange={setDob} max={TODAY_KEY} />
          <div className="field bg-night/70 rounded-xl border border-line px-4 py-2.5">
            <label className="block text-[11px] font-semibold uppercase tracking-wide text-muted">Nationality (country/region) *</label>
            <input type="text" value={nationality} onChange={(e) => setNationality(e.target.value)} className="w-full bg-transparent outline-none text-[15px]" />
          </div>
          <div className="field bg-night/70 rounded-xl border border-line px-4 py-2.5">
            <label className="block text-[11px] font-semibold uppercase tracking-wide text-muted">ID type *</label>
            <SelectMenu value={idType} onChange={setIdType} options={['Passport', 'Aadhaar']} />
          </div>
          <div className="field sm:col-span-2 bg-night/70 rounded-xl border border-line px-4 py-2.5">
            <label className="block text-[11px] font-semibold uppercase tracking-wide text-muted">ID number *</label>
            <input type="text" value={idNumber} onChange={(e) => setIdNumber(e.target.value)} className="w-full bg-transparent outline-none text-[15px]" />
          </div>
        </div>
        <details className="mt-3">
          <summary className="text-sm text-neon font-semibold cursor-pointer">Frequent flyer program (optional)</summary>
          <p className="text-xs text-muted mt-2 rounded-xl border border-line bg-night/40 px-4 py-3">
            <b className="text-warnink">Notice:</b> Please contact the airline to confirm whether miles/points can be earned for this trip.
          </p>
        </details>
        <div className="flex justify-end mt-5">
          <button onClick={save} className="px-8 py-3 rounded-xl bg-[#1C1C1C] text-white font-bold cursor-pointer hover:brightness-125 transition">Save</button>
        </div>
      </div>
    </div>
  );
}
